import React from "react";
import { useT } from "../context/LanguageContext";

/**
 * Interactive piano selector — 12 canonical piano types per the classic reference chart.
 * Each option renders an inline SVG silhouette (no external assets) sized to reflect
 * relative proportions so users can visually tell spinet vs. baby grand vs. concert grand.
 *
 * Categories on the reference chart:
 *   Vertical  : spinet, consolette, console, studio, full_size_professional
 *   Horizontal: petite_grand, baby_grand, medium_grand, semi_concert_grand, concert_grand
 *   Legacy    : square_grand
 *   +         : none (I don't have one yet)
 *
 * Props: value (string), onChange(value)
 */
export const PIANO_TYPES = [
  { id: "spinet",                label: "Spinet",                  blurb: "Smallest vertical — great for apartments.", photo: "/piano-types/spinet.png" },
  { id: "console",               label: "Console",                 blurb: "Direct action, enhanced tone, popular home piano.", photo: "/piano-types/console.png" },
  { id: "studio",                label: "Studio",                  blurb: "Workhorse of music schools — durable, great tone.", photo: "/piano-types/studio.png" },
  { id: "full_size_professional",label: "Full Size / Professional",blurb: "Tallest vertical. Rich tone, built to last generations.", photo: "/piano-types/upright.png" },
  { id: "five_ft_grand",         label: "5ft Grand",               blurb: "5 ft. Smallest grand — apartment- and studio-friendly.", photo: "/piano-types/five_ft_grand.png" },
  { id: "full_size_grand",       label: "Full Size Grand",         blurb: "5′8″ – 6′2″. The classic living-room grand.", photo: "/piano-types/full_size_grand.png" },
  { id: "semi_concert_grand",    label: "Semi-Concert Grand",      blurb: "7 ft. Near-concert scale for larger venues and recital halls.", photo: "/piano-types/semi_concert_grand.png" },
  { id: "concert_grand",         label: "Concert Grand",           blurb: "9 ft+. The concert-hall standard.", photo: "/piano-types/concert_grand.png" },
  { id: "none",                  label: "I don't have a real piano yet 😥", blurb: "That's okay — we'll still track your progress." },
];

/* ---------- Silhouettes ---------- */
/* Each silhouette is drawn on a 120×70 viewBox so they all visually compare to scale. */

const KEYBED = "#FFFFFF";
const KEYBED_STROKE = "#0a0a0a";

function UprightSilhouette({ bodyH, bodyColor = "#6b3a1e", ornate = false }) {
  // bodyH in viewBox units (of 70)
  const top = 60 - bodyH;
  const w = 56;
  const x = (120 - w) / 2;
  return (
    <svg viewBox="0 0 120 70" className="w-full h-20" aria-hidden>
      {/* Body */}
      <rect x={x} y={top} width={w} height={bodyH} rx="2" fill={bodyColor} />
      {/* Music shelf highlight */}
      <rect x={x + 2} y={top + 3} width={w - 4} height="3" fill="#a15d2e" opacity="0.6" />
      {/* Fallboard */}
      <rect x={x + 2} y={60 - 18} width={w - 4} height="4" fill="#3a1c0a" />
      {/* Keybed */}
      <rect x={x + 2} y={60 - 14} width={w - 4} height="8" fill={KEYBED} stroke={KEYBED_STROKE} strokeWidth="0.4" />
      {/* Black keys */}
      {[0,1,3,4,5,7,8].map((i) => (
        <rect key={i} x={x + 4 + i * 4.8} y={60 - 14} width="2" height="4" fill="#0a0a0a" />
      ))}
      {/* Legs */}
      <rect x={x} y="60" width="5" height={ornate ? 8 : 6} fill={bodyColor} />
      <rect x={x + w - 5} y="60" width="5" height={ornate ? 8 : 6} fill={bodyColor} />
      {/* Pedals */}
      <rect x={x + w/2 - 4} y={65} width="8" height="2" fill="#888" />
      {ornate && (
        <>
          {/* Decorative scroll legs */}
          <circle cx={x + 2} cy="66" r="2" fill="#a15d2e" />
          <circle cx={x + w - 2} cy="66" r="2" fill="#a15d2e" />
        </>
      )}
    </svg>
  );
}

function GrandSilhouette({ size = 70, bench = true }) {
  // size represents body length in viewBox units (30..115)
  const x = (120 - size) / 2;
  const curveTop = 20;
  return (
    <svg viewBox="0 0 120 70" className="w-full h-20" aria-hidden>
      {/* Open lid (angled rectangle) */}
      <polygon points={`${x + size * 0.18},${curveTop - 10} ${x + size * 0.55},${curveTop - 14} ${x + size * 0.55},${curveTop} ${x + size * 0.18},${curveTop + 2}`} fill="#1a1a1a" opacity="0.85" />
      {/* Body (harp + rim) — classic rounded-tail shape */}
      <path d={`M ${x} ${curveTop + 18} Q ${x + size * 0.25} ${curveTop - 2} ${x + size * 0.7} ${curveTop + 4} Q ${x + size} ${curveTop + 8} ${x + size} ${curveTop + 24} L ${x + size} ${curveTop + 34} L ${x} ${curveTop + 34} Z`} fill="#0a0a0a" />
      {/* Keybed stripe */}
      <rect x={x + 2} y={curveTop + 30} width={size - 4} height="5" fill={KEYBED} stroke={KEYBED_STROKE} strokeWidth="0.4" />
      {[0,1,3,4,5,7,8,10,11].map((i) => (
        <rect key={i} x={x + 4 + i * ((size - 8) / 12)} y={curveTop + 30} width={size / 40} height="2.2" fill="#0a0a0a" />
      ))}
      {/* Legs */}
      <rect x={x + 4} y={curveTop + 34} width="3" height="18" fill="#0a0a0a" />
      <rect x={x + size - 7} y={curveTop + 34} width="3" height="18" fill="#0a0a0a" />
      <rect x={x + size * 0.35} y={curveTop + 34} width="3" height="16" fill="#0a0a0a" />
      {/* Bench */}
      {bench && (
        <g>
          <rect x={x - 14} y={curveTop + 40} width="12" height="3" fill="#4a2a14" />
          <rect x={x - 12} y={curveTop + 43} width="2" height="7" fill="#4a2a14" />
          <rect x={x - 4} y={curveTop + 43} width="2" height="7" fill="#4a2a14" />
        </g>
      )}
    </svg>
  );
}

function NoneSilhouette() {
  return (
    <div className="h-20 flex items-center justify-center text-4xl" aria-hidden>😥</div>
  );
}

function Silhouette({ id }) {
  switch (id) {
    case "spinet":                 return <UprightSilhouette bodyH={28} bodyColor="#8a4d27" />;
    case "console":                return <UprightSilhouette bodyH={36} bodyColor="#6b3a1e" />;
    case "studio":                 return <UprightSilhouette bodyH={42} bodyColor="#4a2410" />;
    case "full_size_professional": return <UprightSilhouette bodyH={50} bodyColor="#2b1508" />;
    case "five_ft_grand":          return <GrandSilhouette size={60} />;
    case "full_size_grand":        return <GrandSilhouette size={80} />;
    case "semi_concert_grand":     return <GrandSilhouette size={100} />;
    case "concert_grand":          return <GrandSilhouette size={114} />;
    default:                       return <NoneSilhouette />;
  }
}

export default function PianoSelector({ value, onChange }) {
  const t = useT();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="piano-selector">
      {PIANO_TYPES.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            data-testid={`piano-option-${opt.id}`}
            aria-pressed={active}
            className={`text-left rounded-2xl p-3 border-2 transition-all ${active ? "ring-4" : "hover:-translate-y-0.5"}`}
            style={{
              borderColor: active ? "var(--app-accent)" : "var(--app-border)",
              background: active ? "rgba(255,183,3,0.08)" : "var(--app-surface)",
            }}
          >
            <div className="rounded-xl p-2 overflow-hidden flex items-center justify-center" style={{ background: "var(--app-surface-2)", height: "7rem" }}>
              {opt.photo ? (
                <img
                  src={opt.photo}
                  alt={`${t(opt.label)} piano`}
                  className="max-h-full max-w-full object-contain"
                  style={opt.photoPosition ? { objectPosition: opt.photoPosition } : undefined}
                />
              ) : (
                <Silhouette id={opt.id} />
              )}
            </div>
            <div className="mt-2 text-sm font-semibold leading-tight">{t(opt.label)}</div>
            <div className="mt-1 text-xs leading-snug" style={{ color: "var(--app-text-muted)" }}>{t(opt.blurb)}</div>
          </button>
        );
      })}
    </div>
  );
}
